/**
 * @fileoverview QuestionForm Component
 *
 * A comprehensive form component for creating and editing German language learning questions.
 * Supports both single question editing and batch question creation with contexts.
 *
 * Key Features:
 * - Multi-tab interface: Context selection, shared parameters, and individual questions
 * - Support for 6 question types including audio recording
 * - Context management (text, image, audio) with preview
 * - Batch question creation with shared parameters
 * - Comprehensive validation with field-specific error messages
 * - Base64 file encoding for media contexts
 *
 * @author Zaniar
 * @version 1.0.0
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { createQuestionsBatch } from '@/lib/questionApi';
import { convertFileToBase64 } from '@/lib/fileUtils';
import {
  getContextsList,
  getContextById,
  createContext as createContextApi,
  ConfirmationRequiredError,
  type SimilarContext,
  type ContextFormData as ApiContextFormData,
} from '@/lib/contextApi';
import SimilarContextConfirmationDialog from './SimilarContextConfirmationDialog';
import { type Question, type QuestionFormProps } from './questionForm/types';
import { questionTypes } from './questionForm/constants';
import {
  validateForm,
  validateAllQuestions,
} from '@/lib/questionFormValidation';
import { ExpectedAnswerField } from './questionForm/ExpectedAnswerField';
import { QuestionParametersFields } from './questionForm/QuestionParametersFields';
import { QuestionTypeFields } from './questionForm/QuestionTypeFields';
import { ContextSelector } from './questionForm/ContextSelector';
import { ContextUploader } from './questionForm/ContextUploader';
import { ContextPreview } from './questionForm/ContextPreview';
import { logger } from '@/lib/logger';
import {
  isTaskTypeAllowedForModule,
  moduleRequiresTaskType,
} from '@/lib/taskTypeOptions';

type AnswerValue = string | string[] | undefined;

/**
 * Normalize true/false values to handle case sensitivity.
 * Converts "true"/"false" (lowercase) to "True"/"False" (capitalized).
 *
 * @param value - The value to normalize
 * @param questionType - The question type to determine if normalization is needed
 * @returns Normalized value ("True" or "False") or original value
 */
const normalizeExpectedAnswer = (
  value: AnswerValue,
  questionType?: string,
): AnswerValue => {
  if (!value || questionType !== "true-false") {
    return value;
  }
  if (typeof value === "string") {
    const lowerValue = value.toLowerCase();
    if (lowerValue === "true") return "True";
    if (lowerValue === "false") return "False";
  }
  return value;
};

/**
 * Serialize expectedAnswer for API submission.
 * For fill-in-blank questions, converts array to JSON string.
 * For other types, returns as-is.
 *
 * @param value - The expectedAnswer value (string, string[], or array)
 * @param questionType - The question type
 * @returns Serialized value ready for API
 */
const serializeExpectedAnswer = (
  value: AnswerValue,
  questionType?: string,
): string | string[] => {
  if (questionType === 'fill-in-blank') {
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }
    // If it's already a JSON string, return as-is
    if (typeof value === 'string' && value.trim().startsWith('[')) {
      return value;
    }
    // If it's a single string, wrap in array and stringify
    return JSON.stringify([value]);
  }
  // For other types, return as-is
  return value as string | string[];
};

/**
 * Deserialize expectedAnswer when loading from API/database.
 * For fill-in-blank questions, parses JSON string to array.
 * For other types, returns as-is.
 *
 * @param value - The expectedAnswer value from API (string or string[])
 * @param questionType - The question type
 * @returns Deserialized value for form state
 */
const deserializeExpectedAnswer = (
  value: AnswerValue,
  questionType?: string,
): AnswerValue => {
  if (questionType === 'fill-in-blank') {
    if (Array.isArray(value)) {
      // Already an array, ensure it has 5 elements
      const answers = value.slice(0, 5);
      while (answers.length < 5) {
        answers.push('');
      }
      return answers;
    }
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          // Ensure exactly 5 elements
          const answers = parsed.slice(0, 5);
          while (answers.length < 5) {
            answers.push('');
          }
          return answers;
        }
        // If not an array, wrap in array
        return [String(parsed), '', '', '', ''];
      } catch {
        // If not valid JSON, treat as single answer
        return [value, '', '', '', ''];
      }
    }
    // Default: empty array of 5 strings
    return ['', '', '', '', ''];
  }
  // For other types, return as-is
  return value;
};

const normalizeTaskTypeForModule = (
  module: string | undefined,
  taskType: string | null | undefined,
): string | null => {
  if (!moduleRequiresTaskType(module)) {
    return null;
  }
  return isTaskTypeAllowedForModule(module, taskType || undefined)
    ? taskType || ''
    : '';
};

/**
 * QuestionForm Component
 *
 * A sophisticated form component for creating and editing German language exam questions.
 * Features a three-tab workflow for context management, shared parameters, and question details.
 *
 * @param props - Component props
 * @param props.question - Existing question for edit mode (null for creation)
 * @param props.onSave - Callback when form is successfully submitted
 * @param props.onCancel - Callback when user cancels the form
 *
 * @example
 * // Create a new question
 * <QuestionForm onSave={handleSave} onCancel={handleCancel} />
 *
 * @example
 * // Edit existing question
 * <QuestionForm
 *   question={existingQuestion}
 *   onSave={handleUpdate}
 *   onCancel={handleCancel}
 * />
 */
export default function QuestionForm({
  question,
  onSave,
  onCancel,
}: Readonly<QuestionFormProps>) {
  // State for single question edit mode (backward compatibility)
  const [formData, setFormData] = useState<Partial<Question>>({
    text: "",
    type: "multiple-choice-single",
    skill: "",
    topic: "",
    level: "A1",
    isActive: true,
    options: ["", "", "", ""],
    expectedAnswer: "",
    explanation: "",
    taskType: null,
    materialUrl: "",
    materialType: undefined,
    section: 1,
    examTypes: ["goethe"],
    preparationTime: 0,
  });

  // Context-related state
  const [contexts, setContexts] = useState<any[]>([]);
  const [selectedContextId, setSelectedContextId] = useState<number | null>(
    null,
  );
  const [showNewContextForm, setShowNewContextForm] = useState(false);
  const [newContext, setNewContext] = useState({
    name: "",
    type: "text" as "text" | "image" | "audio",
    content: "",
    transcript: "",
  });
  const [loadingContexts, setLoadingContexts] = useState(false);
  const [contextFile, setContextFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewContext, setPreviewContext] = useState<any>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<string>("context");

  // Similar context confirmation dialog state
  const [showSimilarContextDialog, setShowSimilarContextDialog] =
    useState(false);
  const [similarContextData, setSimilarContextData] = useState<{
    similarContexts: SimilarContext[];
    maxSimilarity: number;
    threshold: number;
    message: string;
  } | null>(null);
  const [pendingContextData, setPendingContextData] =
    useState<ApiContextFormData | null>(null);

  // Shared parameters for all questions in batch
  const [sharedParams, setSharedParams] = useState({
    level: "A1",
    skill: "reading",
    taskType: null as string | null,
    section: 1,
    examTypes: ["goethe"],
  });

  // Individual question data (without shared params)
  const [questions, setQuestions] = useState<Partial<Question>[]>([
    {
      text: "",
      type: "multiple-choice-single",
      topic: "",
      isActive: true,
      options: ["", "", "", ""],
      expectedAnswer: "",
      explanation: "",
      materialUrl: "",
      materialType: undefined,
      preparationTime: 0,
      recordingDuration: undefined,
    },
  ]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Define tab navigation order
  const tabs = ["context", "shared-params", "questions"];

  /**
   * Navigate to the next tab in the form workflow.
   * Used for sequential form progression through Context → Shared Parameters → Questions.
   */
  const goToNextTab = () => {
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  /**
   * Navigate to the previous tab in the form workflow.
   * Allows users to go back and modify previously entered data.
   */
  const goToPreviousTab = () => {
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  /**
   * Add a new empty question to the batch creation array.
   * Initializes a question with default values and adds it to the questions array.
   * Automatically switches the current question index to the newly added question.
   */
  const addNewQuestion = () => {
    const newQuestion: Partial<Question> = {
      text: "",
      type: "multiple-choice-single",
      topic: "",
      isActive: true,
      options: ["", "", "", ""],
      expectedAnswer: "",
      explanation: "",
      materialUrl: "",
      materialType: undefined,
      preparationTime: 0,
      recordingDuration: undefined,
    };
    setQuestions([...questions, newQuestion]);
    setCurrentQuestionIndex(questions.length);
  };

  /**
   * Remove a question from the batch creation array.
   * Prevents removal if only one question remains to ensure at least one question exists.
   * Adjusts current question index if necessary.
   *
   * @param {number} index - Index of the question to remove
   */
  const removeQuestion = (index: number) => {
    if (questions.length > 1) {
      const newQuestions = questions.filter((_, i) => i !== index);
      setQuestions(newQuestions);
      if (currentQuestionIndex >= newQuestions.length) {
        setCurrentQuestionIndex(newQuestions.length - 1);
      }
    }
  };

  /**
   * Update the current question with new values.
   * Used during batch creation to modify individual question properties.
   * Implements immutable update pattern for React state management.
   *
   * @param {Partial<Question>} updates - Object containing fields to update
   */
  const updateCurrentQuestion = (updates: Partial<Question>) => {
    const newQuestions = [...questions];
    newQuestions[currentQuestionIndex] = {
      ...newQuestions[currentQuestionIndex],
      ...updates,
    };
    setQuestions(newQuestions);
  };

  /**
   * Get the currently active question from the batch creation array.
   *
   * @returns {Partial<Question>} Current question object
   */
  const getCurrentQuestion = () => questions[currentQuestionIndex];

  /**
   * Create a new context and automatically navigate to the next tab.
   * Combines context creation with workflow progression for better UX.
   *
   * @param {React.FormEvent} e - Form event to prevent default submission
   * @async
   */
  const handleCreateContextAndNext = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleCreateContext();
    // After creating context, go to next tab
    goToNextTab();
  };

  /**
   * Load contexts from API on component mount.
   * Runs once when the component first renders.
   */
  useEffect(() => {
    loadContexts();
  }, []);

  /**
   * Initialize form data when editing an existing question.
   * Converts question data to form-compatible format.
   * Sets associated context if question has one.
   */
  useEffect(() => {
    if (question) {
      setFormData({
        ...question,
        options: question.options || ["", "", "", ""],
        // Convert capitalized skill back to lowercase for form compatibility
        skill: question.skill?.toLowerCase() || "reading",
        // Ensure preparationTime has a valid default value
        preparationTime: question.preparationTime ?? 0,
        // Normalize/deserialize expectedAnswer based on question type
        expectedAnswer: question.type === 'fill-in-blank'
          ? deserializeExpectedAnswer(question.expectedAnswer, question.type)
          : normalizeExpectedAnswer(
              question.expectedAnswer,
              question.type,
            ),
      });

      // Set the selected context if the question has one
      if (question.context?.id) {
        setSelectedContextId(question.context.id);
      } else {
        setSelectedContextId(null);
      }
    }
  }, [question]);

  /**
   * Set default expected answer for true-false and fill-in-blank questions.
   * Automatically defaults to 'True' when question type changes to true-false.
   * Initializes array of 5 empty strings for fill-in-blank questions.
   */
  useEffect(() => {
    if (formData.type === "true-false" && !formData.expectedAnswer) {
      setFormData((prev) => ({
        ...prev,
        expectedAnswer: "True",
      }));
    } else if (formData.type === "fill-in-blank") {
      // Initialize fill-in-blank with array of 5 empty strings if not already set
      const currentAnswer = formData.expectedAnswer;
      if (!currentAnswer || (typeof currentAnswer === 'string' && !currentAnswer.trim().startsWith('['))) {
        setFormData((prev) => ({
          ...prev,
          expectedAnswer: ['', '', '', '', ''],
        }));
      } else if (Array.isArray(currentAnswer) && currentAnswer.length !== 5) {
        // Ensure exactly 5 elements
        const answers = [...currentAnswer];
        while (answers.length < 5) {
          answers.push('');
        }
        setFormData((prev) => ({
          ...prev,
          expectedAnswer: answers.slice(0, 5),
        }));
      }
    }
  }, [formData.type]);

  /**
   * Load all available contexts from the API.
   * Fetches the list of contexts and updates the local state.
   * Displays loading state and error notifications.
   *
   * @async
   */
  const loadContexts = async () => {
    setLoadingContexts(true);
    try {
      const fetchedContexts = await getContextsList();
      setContexts(fetchedContexts);
    } catch (error) {
      toast.error("Failed to load contexts");
      logger.error("Error loading contexts:", error);
    } finally {
      setLoadingContexts(false);
    }
  };

  /**
   * Load full context details for preview dialog.
   * Fetches complete binary content and metadata for display.
   * Used when user clicks the preview button on a context card.
   *
   * @param {number} contextId - ID of the context to preview
   * @async
   */
  const loadFullContextForPreview = async (contextId: number) => {
    try {
      const fullContext = await getContextById(contextId);
      setPreviewContext(fullContext);
    } catch (error) {
      toast.error("Failed to load context preview");
      logger.error("Error loading context preview:", error);
    }
  };

  /**
   * Handle file upload for context creation.
   * Validates file type based on context type (image/audio).
   * Updates state with selected file and displays success notification.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - File input change event
   */
  const handleContextFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setContextFile(file);

      // Validate file type
      if (newContext.type === "image" && !file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        setContextFile(null);
        return;
      }

      if (newContext.type === "audio" && !file.type.startsWith("audio/")) {
        toast.error("Please select an audio file");
        setContextFile(null);
        return;
      }

      toast.success(`File "${file.name}" selected successfully!`);
    }
  };

  /**
   * Create a new context with the provided data.
   * Handles text, image, and audio contexts differently.
   * For media contexts, converts files to base64 before submission.
   *
   * @async
   */
  const handleCreateContext = async () => {
    if (!newContext.name.trim()) {
      toast.error("Please enter a context name");
      return;
    }

    // Validate based on type
    if (newContext.type === "text" && !newContext.content.trim()) {
      toast.error("Please enter text content");
      return;
    }

    if (
      (newContext.type === "image" || newContext.type === "audio") &&
      !contextFile
    ) {
      toast.error(`Please upload ${newContext.type} file`);
      return;
    }

    setUploading(true);
    let contextData: ApiContextFormData;
    try {
      if (newContext.type === "text") {
        contextData = {
          name: newContext.name,
          type: newContext.type,
          content: newContext.content,
        };
      } else {
        // For image/audio, convert file to base64
        if (!contextFile) {
          toast.error(`Please upload ${newContext.type} file`);
          return;
        }
        const base64Content = await convertFileToBase64(contextFile);

        contextData = {
          name: newContext.name,
          type: newContext.type,
          content: base64Content,
          fileName: contextFile.name,
          mimeType: contextFile.type,
          transcript:
            newContext.type === "audio" ? newContext.transcript : undefined,
        };
      }

      const createdContext = await createContextApi(contextData);

      setContexts([createdContext, ...contexts]);
      setSelectedContextId(createdContext.id);
      setNewContext({ name: "", type: "text", content: "", transcript: "" });
      setContextFile(null);
      setShowNewContextForm(false);
      toast.success("Context created successfully!");
    } catch (error: any) {
      logger.error("Error creating context:", error);
      // Check if error is ConfirmationRequiredError
      if (error instanceof ConfirmationRequiredError) {
        // Use contextData that was already prepared (file already converted to base64)
        // Show confirmation dialog
        setSimilarContextData({
          similarContexts: error.similarContexts,
          maxSimilarity: error.maxSimilarity,
          threshold: error.threshold,
          message: error.message,
        });
        setPendingContextData(contextData);
        setShowSimilarContextDialog(true);
      } else {
        toast.error(
          `Failed to create context: ${error.message || "Unknown error"}`,
        );
      }
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle confirmation to save context despite similarity
   */
  const handleConfirmSave = async () => {
    if (!pendingContextData) {
      return;
    }

    setUploading(true);
    try {
      // Resend request with confirmSave: true
      const contextDataWithConfirm = {
        ...pendingContextData,
        confirmSave: true,
      };

      const createdContext = await createContextApi(contextDataWithConfirm);

      setContexts([createdContext, ...contexts]);
      setSelectedContextId(createdContext.id);
      setNewContext({ name: "", type: "text", content: "", transcript: "" });
      setContextFile(null);
      setShowNewContextForm(false);
      toast.success("Context created successfully!");
      setShowSimilarContextDialog(false);
      setSimilarContextData(null);
      setPendingContextData(null);
    } catch (error: any) {
      logger.error("Error saving context:", error);
      toast.error(
        `Failed to save context: ${error.message || "Unknown error"}`,
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle cancellation of save
   */
  const handleCancelSave = () => {
    setShowSimilarContextDialog(false);
    setSimilarContextData(null);
    setPendingContextData(null);
  };

  /**
   * Handle generic input field changes with intelligent routing.
   * Routes updates to either single edit mode or batch creation mode.
   * Automatically sets default values for certain question types.
   * Clears validation errors when user starts typing.
   *
   * @param {string} field - Field name to update
   * @param {any} value - New value for the field
   */
  const handleInputChange = (field: string, value: any) => {
    // For editing single question (backward compatibility)
    if (question) {
      setFormData((prev) => {
        const updates: Partial<Question> = { [field]: value };
        if (field === "skill") {
          updates.taskType = normalizeTaskTypeForModule(value, prev.taskType);
        }
        return {
          ...prev,
          ...updates,
        };
      });

      // Set default expected answer for true-false questions
      if (field === "type" && value === "true-false") {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
          expectedAnswer: "True",
        }));
      }
      // Initialize fill-in-blank with array of 5 empty strings
      else if (field === "type" && value === "fill-in-blank") {
        setFormData((prev) => ({
          ...prev,
          [field]: value,
          expectedAnswer: ['', '', '', '', ''],
        }));
      }
    } else if (["level", "skill", "taskType", "section", "examTypes"].includes(field)) {
      // Check if this is a shared parameter
      setSharedParams((prev) => {
        const next = {
          ...prev,
          [field]: value,
        };
        if (field === "skill") {
          next.taskType = normalizeTaskTypeForModule(value, prev.taskType);
        }
        return next;
      });
    } else {
      // For bulk creation - update current question
      const updates: Partial<Question> = { [field]: value };

      // Set default expected answer for true-false questions
      if (field === "type" && value === "true-false") {
        updates.expectedAnswer = "True";
      }
      // Initialize fill-in-blank with array of 5 empty strings
      else if (field === "type" && value === "fill-in-blank") {
        updates.expectedAnswer = ['', '', '', '', ''];
      }

      updateCurrentQuestion(updates);
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  /**
   * Update a specific answer option in the options array.
   * Used for multiple choice questions to modify individual options.
   *
   * @param {number} index - Index of the option to update
   * @param {string} value - New text value for the option
   */
  const handleOptionChange = (index: number, value: string) => {
    const currentQ = getCurrentQuestion();
    const newOptions = [...(currentQ.options || ["", "", "", ""])];
    newOptions[index] = value;
    updateCurrentQuestion({ options: newOptions });
  };

  /**
   * Add a new empty answer option to the options array.
   * Dynamically extends the multiple choice options list.
   */
  const addOption = () => {
    const currentQ = getCurrentQuestion();
    updateCurrentQuestion({
      options: [...(currentQ.options || []), ""],
    });
  };

  /**
   * Remove an answer option from the options array.
   * Prevents removal if less than 2 options would remain.
   *
   * @param {number} index - Index of the option to remove
   */
  const removeOption = (index: number) => {
    const currentQ = getCurrentQuestion();
    const newOptions = (currentQ.options || []).filter((_, i) => i !== index);
    updateCurrentQuestion({ options: newOptions });
  };

  /**
   * Handle exam type checkbox changes.
   * Toggles exam types in the selected exam types array.
   * Works with both single edit and batch creation modes.
   *
   * @param {string} examType - Exam type identifier (goethe, ösd, telc)
   * @param {boolean} checked - Whether the checkbox is checked
   */
  const handleExamTypeChange = (examType: string, checked: boolean) => {
    // For single edit mode (backward compatibility)
    if (question) {
      const currentExamTypes = formData.examTypes || [];
      let newExamTypes: string[];

      if (checked) {
        newExamTypes = [...currentExamTypes, examType];
      } else {
        newExamTypes = currentExamTypes.filter((type) => type !== examType);
      }

      setFormData((prev) => ({ ...prev, examTypes: newExamTypes }));
    } else {
      // For bulk creation, exam types are shared
      const currentExamTypes = sharedParams.examTypes || [];
      let newExamTypes: string[];

      if (checked) {
        newExamTypes = [...currentExamTypes, examType];
      } else {
        newExamTypes = currentExamTypes.filter((type) => type !== examType);
      }

      setSharedParams((prev) => ({ ...prev, examTypes: newExamTypes }));
    }
  };

  /**
   * Validate form data for single question edit mode.
   * Performs comprehensive validation based on question type.
   * Sets error messages for invalid fields and returns validation status.
   *
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateFormLocal = (): boolean => {
    const result = validateForm(formData);
    setErrors(result.errors);
    return result.isValid;
  };

  /**
   * Validate all questions in batch creation mode.
   * Validates both shared parameters and individual questions.
   * Generates prefixed error keys for proper display on relevant fields.
   *
   * @returns {boolean} True if all questions are valid, false otherwise
   */
  const validateAllQuestionsLocal = (): boolean => {
    const result = validateAllQuestions(sharedParams, questions);
    setErrors(result.errors);
    return result.isValid;
  };

  /**
   * Handle submission for single question edit mode.
   */
  const handleSingleQuestionSubmit = () => {
    if (!validateFormLocal()) {
      toast.error("Please fix the errors before saving");
      return;
    }

    const questionData = {
      ...formData,
      contextId: selectedContextId,
      module: formData.skill?.toLowerCase() || "reading",
      taskType: formData.taskType || null,
      materialText:
        formData.materialUrl && !formData.materialUrl.startsWith("http")
          ? formData.materialUrl
          : null,
      materialType: formData.materialType || undefined,
      options: formData.options
        ? formData.options.filter((opt) => opt.trim())
        : undefined,
      preparationTime: formData.preparationTime ?? 0,
      recordingDuration: formData.recordingDuration,
      // Serialize expectedAnswer for fill-in-blank questions
      expectedAnswer: serializeExpectedAnswer(
        formData.expectedAnswer,
        formData.type,
      ),
    };

    onSave(questionData);
    toast.success("Question updated successfully!");
  };

  /**
   * Handle submission for bulk question creation mode.
   */
  const handleBulkQuestionSubmit = async () => {
    if (!selectedContextId) {
      toast.error('Please select a context');
      return;
    }

    if (!validateAllQuestionsLocal()) {
      toast.error("Please fix all validation errors before saving");
      return;
    }

    try {
      // Prepare all questions for batch creation, merging shared params with individual questions
      const questionsToCreate = questions.map((q) => ({
        ...q,
        // Add shared parameters to each question
        level: sharedParams.level,
        skill: sharedParams.skill,
        taskType: sharedParams.taskType || null,
        section: sharedParams.section,
        examTypes: sharedParams.examTypes,
        // Process individual question data
        module: sharedParams.skill?.toLowerCase() || "reading",
        materialText:
          q.materialUrl && !q.materialUrl.startsWith("http")
            ? q.materialUrl
            : null,
        materialType: q.materialType || undefined,
        options: q.options
          ? q.options.filter((opt: string) => opt.trim())
          : undefined,
        preparationTime: q.preparationTime ?? 0,
        recordingDuration: q.recordingDuration,
        // Serialize expectedAnswer for fill-in-blank questions
        expectedAnswer: serializeExpectedAnswer(q.expectedAnswer, q.type),
        contextId: selectedContextId?.toString(),
      }));

      const result = await createQuestionsBatch(
        selectedContextId.toString(),
        questionsToCreate,
      );

      toast.success(`${result.count} questions created successfully!`);
      onSave(result); // Pass result to parent component
    } catch (error) {
      logger.error("Error creating questions in batch:", error);
      toast.error("Failed to create questions");
    }
  };

  /**
   * Handle form submission for both single edit and batch creation modes.
   * Performs validation, transforms data, and calls appropriate API methods.
   * Displays success/error notifications and invokes parent callbacks.
   *
   * @param {React.FormEvent} e - Form submission event
   * @async
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (question) {
      handleSingleQuestionSubmit();
    } else {
      await handleBulkQuestionSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="context">
            <span className="font-ui">Context</span>
          </TabsTrigger>
          <TabsTrigger value="shared-params">
            <span className="font-ui">Shared Parameters</span>
          </TabsTrigger>
          <TabsTrigger value="questions">
            <span className="font-ui">Questions</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Context Selection/Creation */}
        <TabsContent value="context" className="space-y-4">
          {showNewContextForm && (
            <ContextUploader
              newContext={newContext}
              contextFile={contextFile}
              uploading={uploading}
              onInputChange={(field, value) => {
                if (field === "file") {
                  setContextFile(null);
                } else {
                  setNewContext((prev) => ({ ...prev, [field]: value }));
                }
              }}
              onFileUpload={handleContextFileUpload}
              onCancel={() => {
                setShowNewContextForm(false);
                setContextFile(null);
                setNewContext({
                  name: "",
                  type: "text",
                  content: "",
                  transcript: "",
                });
              }}
              onTypeChange={(type) => {
                setNewContext((prev) => ({
                  ...prev,
                  type,
                  content: "",
                  transcript: "",
                }));
                setContextFile(null);
              }}
            />
          )}
          <ContextSelector
            contexts={contexts}
            selectedContextId={selectedContextId}
            loadingContexts={loadingContexts}
            showNewContextForm={showNewContextForm}
            onSelect={(contextId) => setSelectedContextId(contextId)}
            onClear={() => setSelectedContextId(null)}
            onCreateNew={() => setShowNewContextForm(true)}
            onPreview={loadFullContextForPreview}
            onNext={goToNextTab}
            onCreateAndNext={
              showNewContextForm
                ? () => {
                    handleCreateContextAndNext({
                      preventDefault: () => {},
                    } as React.FormEvent);
                  }
                : undefined
            }
            uploading={uploading}
            canCreateAndNext={
              showNewContextForm &&
              !!newContext.name &&
              !!newContext.type &&
              !!(newContext.content || contextFile)
            }
          />
        </TabsContent>

        {/* Tab 2: Shared Parameters */}
        <TabsContent value="shared-params" className="space-y-4">
          {!question && (
            <>
              {/* Shared Parameters Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-ui text-lg">
                    Shared Parameters
                  </CardTitle>
                  <CardDescription className="font-ui">
                    These parameters will be applied to all questions in this batch
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <QuestionParametersFields
                    level={sharedParams.level}
                    skill={sharedParams.skill}
                    taskType={sharedParams.taskType}
                    section={sharedParams.section}
                    examTypes={sharedParams.examTypes}
                    onChange={handleInputChange}
                    errors={errors}
                    mode="shared"
                    onExamTypeChange={(examType, checked) =>
                      handleExamTypeChange(examType, checked)
                    }
                  />
                </CardContent>
              </Card>

              {/* Navigation Buttons for Shared Parameters Tab */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousTab}
                >
                  <span className="font-ui">Back</span>
                </Button>
                <Button type="button" onClick={goToNextTab}>
                  <span className="font-ui">Next</span>
                </Button>
              </div>
            </>
          )}

          {/* Single question edit mode - show shared params as read-only */}
          {question && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="font-ui">
                    Question Parameters
                  </CardTitle>
                  <CardDescription className="font-ui">
                    Set the parameters for this question
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <QuestionParametersFields
                    level={formData.level || "A1"}
                    skill={formData.skill || "reading"}
                    taskType={formData.taskType}
                    section={formData.section || 1}
                    examTypes={formData.examTypes || []}
                    onChange={handleInputChange}
                    errors={errors}
                    mode="single"
                    showType={true}
                    questionType={formData.type}
                    onExamTypeChange={handleExamTypeChange}
                  />
                </CardContent>
              </Card>

              {/* Navigation Buttons for Shared Parameters Tab */}
              <div className="flex justify-between pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousTab}
                >
                  <span className="font-ui">Back</span>
                </Button>
                <Button type="button" onClick={goToNextTab}>
                  <span className="font-ui">Next</span>
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Tab 3: Questions Management */}
        <TabsContent value="questions" className="space-y-4">
          {!question && (
            <>
              {/* Questions List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-ui text-lg font-semibold">
                    Questions ({questions.length})
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addNewQuestion}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="font-ui">Add Another Question</span>
                  </Button>
                </div>

                {/* Question Selector Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {questions.map((q, idx) => {
                    // Create a stable key using question properties
                    const questionKey = `question-${q.type || 'default'}-${q.text?.substring(0, 20) || 'empty'}-${idx}`;
                    return (
                      <Button
                        key={questionKey}
                        type="button"
                        variant={
                          currentQuestionIndex === idx ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setCurrentQuestionIndex(idx)}
                        className="flex-shrink-0"
                      >
                        <span className="font-ui">Question {idx + 1}</span>
                        {questions.length > 1 && (
                          <button
                            type="button"
                            className="ml-2 cursor-pointer hover:text-destructive hover:bg-destructive/10 rounded p-1"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeQuestion(idx);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                removeQuestion(idx);
                              }
                            }}
                            aria-label={`Remove question ${idx + 1}`}
                            tabIndex={0}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Current Question Editor */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="font-ui">
                      Question {currentQuestionIndex + 1}
                    </span>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id={`active-switch-${currentQuestionIndex}`}
                          checked={getCurrentQuestion().isActive !== false}
                          onCheckedChange={(checked) =>
                            handleInputChange("isActive", checked)
                          }
                        />
                        <Label
                          htmlFor={`active-switch-${currentQuestionIndex}`}
                        >
                          <span className="font-ui">Active</span>
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant="secondary">{sharedParams.level}</Badge>
                        <Badge variant="secondary">{sharedParams.skill}</Badge>
                      </div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Question Type */}
                  <div>
                    <Label htmlFor="type" className="font-ui">
                      Question Type
                    </Label>
                    <Select
                      value={
                        getCurrentQuestion().type || "multiple-choice-single"
                      }
                      onValueChange={(value) =>
                        handleInputChange("type", value)
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {questionTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Question Content */}
                  <div>
                    <Label htmlFor="text" className="font-ui">
                      Question Text
                    </Label>
                    <Textarea
                      id="text"
                      value={getCurrentQuestion().text || ""}
                      onChange={(e) =>
                        handleInputChange("text", e.target.value)
                      }
                      placeholder="Enter the question text..."
                      className="mt-1 min-h-[200px]"
                      rows={8}
                    />
                    {errors[`question_${currentQuestionIndex}_text`] && (
                      <p className="font-ui text-sm text-destructive mt-1">
                        {errors[`question_${currentQuestionIndex}_text`]}
                      </p>
                    )}
                  </div>

                  <QuestionTypeFields
                    questionType={getCurrentQuestion().type}
                    questionData={getCurrentQuestion()}
                    errors={{
                      ...Object.fromEntries(
                        Object.entries(errors)
                          .filter(
                            ([key]) =>
                              key.startsWith(
                                `question_${currentQuestionIndex}_`,
                              ) ||
                              (!key.startsWith("question_") &&
                                !key.startsWith("shared_")),
                          )
                          .map(([key, value]) => [
                            key.replace(
                              `question_${currentQuestionIndex}_`,
                              "",
                            ),
                            value,
                          ]),
                      ),
                    }}
                    handleInputChange={handleInputChange}
                    handleOptionChange={handleOptionChange}
                    addOption={addOption}
                    removeOption={removeOption}
                    isEditMode={false}
                  />

                  <div>
                    <Label htmlFor="expectedAnswer" className="font-ui">
                      Expected Answer
                    </Label>
                    <ExpectedAnswerField
                      questionType={getCurrentQuestion().type}
                      value={getCurrentQuestion().expectedAnswer || ""}
                      onChange={(value) =>
                        handleInputChange("expectedAnswer", value)
                      }
                      options={getCurrentQuestion().options}
                      idPrefix={currentQuestionIndex.toString()}
                    />
                    {errors[
                      `question_${currentQuestionIndex}_expectedAnswer`
                    ] && (
                      <p className="font-ui text-sm text-destructive mt-1">
                        {
                          errors[
                            `question_${currentQuestionIndex}_expectedAnswer`
                          ]
                        }
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="explanation" className="font-ui">
                      Explanation
                    </Label>
                    <Textarea
                      id="explanation"
                      value={getCurrentQuestion().explanation || ""}
                      onChange={(e) =>
                        handleInputChange("explanation", e.target.value)
                      }
                      placeholder="Enter the explanation for the expected answer..."
                      className="mt-1"
                      rows={3}
                    />
                    {errors[`question_${currentQuestionIndex}_explanation`] && (
                      <p className="font-ui text-sm text-destructive mt-1">
                        {errors[`question_${currentQuestionIndex}_explanation`]}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="font-ui">Additional Material (Optional)</Label>
                    <Textarea
                      value={getCurrentQuestion().materialUrl || ""}
                      onChange={(e) =>
                        handleInputChange("materialUrl", e.target.value)
                      }
                      placeholder="Enter additional material URL or text..."
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Action buttons for bulk creation */}
              <div className="flex items-center justify-between pt-6 border-t mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousTab}
                >
                  <span className="font-ui">Back</span>
                </Button>
                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={onCancel}>
                    <X className="h-4 w-4 mr-2" />
                    <span className="font-ui">Cancel</span>
                  </Button>
                  <Button type="submit">
                    <Save className="h-4 w-4 mr-2" />
                    <span className="font-ui">
                      {questions.length > 1
                        ? `Create ${questions.length} Questions`
                        : `Create Question`}
                    </span>
                  </Button>
                </div>
              </div>
            </>
          )}

          {/* Single question edit mode (backward compatibility) */}
          {question && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/20">
                <div className="space-y-0.5">
                  <Label htmlFor="active-mode" className="font-ui text-base">
                    Active Status
                  </Label>
                  <p className="font-ui text-sm text-muted-foreground">
                    Active questions are visible to students
                  </p>
                </div>
                <Switch
                  id="active-mode"
                  checked={formData.isActive !== false}
                  onCheckedChange={(checked) =>
                    handleInputChange("isActive", checked)
                  }
                />
              </div>

              <div>
                <Label htmlFor="text" className="font-ui">
                  Question Text
                </Label>
                <Textarea
                  id="text"
                  value={formData.text || ""}
                  onChange={(e) => handleInputChange("text", e.target.value)}
                  placeholder="Enter the question text..."
                  className="mt-1 min-h-[200px]"
                  rows={8}
                />
                {errors.text && (
                  <p className="text-sm text-destructive mt-1">{errors.text}</p>
                )}
              </div>

              <QuestionTypeFields
                questionType={formData.type}
                questionData={formData}
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                handleOptionChange={(index, value) => {
                  const newOptions = [
                    ...(formData.options || ["", "", "", ""]),
                  ];
                  newOptions[index] = value;
                  setFormData((prev) => ({ ...prev, options: newOptions }));
                }}
                addOption={() => {
                  setFormData((prev) => ({
                    ...prev,
                    options: [...(prev.options || []), ""],
                  }));
                }}
                removeOption={(index) => {
                  const newOptions = (formData.options || []).filter(
                    (_, i) => i !== index,
                  );
                  setFormData((prev) => ({ ...prev, options: newOptions }));
                }}
                isEditMode={true}
              />

              <div>
                <Label htmlFor="expectedAnswer" className="font-ui">
                  Expected Answer
                </Label>
                <ExpectedAnswerField
                  questionType={formData.type}
                  value={formData.expectedAnswer || ""}
                  onChange={(value) =>
                    handleInputChange("expectedAnswer", value)
                  }
                  options={formData.options}
                />
                {errors.expectedAnswer && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.expectedAnswer}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="explanation" className="font-ui">
                  Explanation
                </Label>
                <Textarea
                  id="explanation"
                  value={formData.explanation || ""}
                  onChange={(e) =>
                    handleInputChange("explanation", e.target.value)
                  }
                  placeholder="Enter the explanation for the expected answer..."
                  className="mt-1"
                  rows={3}
                />
                {errors.explanation && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.explanation}
                  </p>
                )}
              </div>

              <div>
                <Label className="font-ui">Additional Material (Optional)</Label>
                <Textarea
                  value={formData.materialUrl || ""}
                  onChange={(e) =>
                    handleInputChange("materialUrl", e.target.value)
                  }
                  placeholder="Enter additional material URL or text..."
                  className="mt-2"
                  rows={3}
                />
              </div>

              {/* Action buttons for single question edit */}
              <div className="flex items-center justify-between pt-6 border-t mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousTab}
                >
                  <span className="font-ui">Back</span>
                </Button>
                <div className="flex gap-4">
                  <Button type="button" variant="outline" onClick={onCancel}>
                    <X className="h-4 w-4 mr-2" />
                    <span className="font-ui">Cancel</span>
                  </Button>
                  <Button type="submit">
                    <Save className="h-4 w-4 mr-2" />
                    <span className="font-ui">Update Question</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Context Preview Dialog */}
      {previewContext && (
        <ContextPreview
          previewContext={previewContext}
          onClose={() => setPreviewContext(null)}
          onSelect={(contextId) => {
            setSelectedContextId(contextId);
            setPreviewContext(null);
          }}
        />
      )}

      {/* Similar Context Confirmation Dialog */}
      {similarContextData && (
        <SimilarContextConfirmationDialog
          open={showSimilarContextDialog}
          onOpenChange={setShowSimilarContextDialog}
          similarContexts={similarContextData.similarContexts}
          maxSimilarity={similarContextData.maxSimilarity}
          threshold={similarContextData.threshold}
          message={similarContextData.message}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelSave}
        />
      )}
    </form>
  );
}
